// PARTNER-PORTÁL · Jegyeim (lista) — a terv szerint
// (a partner-terv szerint — a MEGÉPÜLT állapot: `docs/foundations/design.md` §9).
//
// A TOOLBAR: fül-váltó darabszámokkal · EGY kereső · szűrő-popover (fiók + típus) · találat-szám · „Új jegy".
// MIÉRT EGY KERESŐ (a legacyben öt oszlop-szűrő volt): a partnernek jellemzően néhány tucat jegye van — öt
// szabad-szöveges mező ott zaj. A szűkítést a FIÓK és a TÍPUS választója viszi, ezek pedig kevés, ismert
// értékkészletűek.
//
// A TÁBLA a KÖZÖS `DataTable` (CSS-grid + ARIA) — a terv `.dt*` osztályai pontosan ezt írják le. Az olvasatlan
// sor bal szélén brand-jelölő sáv, a szerver pedig az olvasatlanokat teszi előre (legacy munka-sorrend), ezért
// a UI NEM rendez újra.
import type { PortalTicketRow } from '@atlas/contracts';
import { PORTAL_PERMISSIONS } from '@atlas/shared';
import {
  ArrowPathIcon,
  ChatBubbleLeftRightIcon,
  CheckCircleIcon,
  MagnifyingGlassIcon,
  PlusIcon,
} from '@heroicons/react/24/outline';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { PortalErrorState } from '@/components/portal/portal-error-state';
import { PortalPermissionGate } from '@/components/portal/portal-permission-gate';
import { DataTable, type DataTableColumn } from '@/components/shared/data-table';
import { DetailsExpander } from '@/components/shared/details-expander';
import { EmptyState } from '@/components/shared/empty-state';
import { ListToolbar } from '@/components/shared/list-toolbar';
import { PersonChip } from '@/components/shared/person-chip';
import { TicketStatusBadge } from '@/components/tickets/ticket-status-badge';
import { TicketTypeBadge } from '@/components/tickets/ticket-type-badge';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { TabStrip } from '@/components/ui/tab-strip';
import { Text } from '@/components/ui/text';
import { portalKeys } from '@/features/portal/api';
import { usePortal, usePortalHead } from '@/features/portal/portal-context';
import { PORTAL_TICKET_STATUSES, PORTAL_TICKET_TYPES } from '@/features/portal/ticket-types';
import { usePortalPermissions } from '@/features/portal/use-portal-permissions';
import { useListFilters } from '@/hooks/use-list-filters';
import { api } from '@/lib/api';
import { formatRelativeTime } from '@/lib/format-datetime';
import { formatName } from '@/lib/format-name';
import { cn } from '@/lib/utils';

/** A szűrők/fül URL-sémája — a router csak ISMERT kulcsokat tart meg, ezért itt fel kell sorolni őket. */
const searchSchema = z.object({
  tab: z.enum(['in_progress', 'closed']).optional(),
  q: z.string().optional(),
  type: z.string().optional(),
  // A `status` KORÁBBAN KIMARADT innen, holott a `DEFAULTS`-ban szerepel — a router pedig CSAK az itt
  // felsorolt kulcsokat tartja meg, tehát a státusz-szűrő azonnal visszaállt „mind"-re, és a választás nem
  // volt linkelhető. (Mérve; ugyanez a csapda a `apps/web/CLAUDE.md`-ben is le van írva.)
  status: z.string().optional(),
  // LÉTREHOZÁS-dátum tartomány (-tól/-ig) — a belső jegy-listával azonos szűrő-pár.
  from: z.string().optional(),
  to: z.string().optional(),
  // LEZÁRÁS-dátum tartomány — csak a „Lezárt" fülön (a belső listával azonos).
  closedFrom: z.string().optional(),
  closedTo: z.string().optional(),
});

/** A `ListToolbar` „mind" szentinelje (a select-nek NEM lehet üres string az értéke). */
const ALL = 'all';

/** ISO → helyi DÁTUM (idő nélkül); érvénytelen/hiányzó → üres string (a sor ilyenkor kimarad). */
function openedDate(iso: string | null, locale: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString(locale);
}

/**
 * A „Részletek" kibontó (és a mobil-kártya részlet-rácsa) sorai: a JEGYSZÁM, a NYITÁS dátuma és az IGÉNYLŐ.
 *
 * MIÉRT ITT, MODUL-SZINTEN: ugyanezeket a párokat a desktop-kibontó ÉS a mobil-kártya is kéri (a `DataTable`
 * `cardExtraItems`-e) — egy helyen tartva nem tud elcsúszni a kettő. A jegyszám `font-mono` + `tabular-nums`:
 * itt már NEM horgony, hanem KÓD, amit karakterenként vetnek össze (a horgony a tárgy).
 *
 * AZ IGÉNYLŐ SZÁNDÉKOSAN CSAK NÉV, KÉP NÉLKÜL (user-döntés): a részlet-panel `label: value`
 * párok rácsa, egységes 12/muted tipográfiával — egy avatar itt kilógna a rácsból, és a partner-oldalon a
 * saját fiók kapcsolattartóiról van szó, akiket a név azonosít. A NÉVSORREND viszont NYELV-FÜGGŐ
 * (`formatName`): magyarul vezetéknév elöl. Ezért kell a szervertől a NÉV-RÉSZEK (`requesterGivenName` /
 * `requesterFamilyName`) — az összefűzött `requesterName` csak fallback, ha a részek nem oldhatók fel.
 */
function detailRows(r: Row, t: TFunction, locale: string): { label: string; value: string }[] {
  return [
    // A VALUE SIMA SZÖVEG (nem saját tipográfiájú span): így a `DetailsExpander` egységes stílusát kapja
    // (12 / muted, a címke `font-medium`) — PONTOSAN úgy, ahogy a BELSŐ jegy-lista `ticketDetailRows`-a
    // (user-döntés: a két felület részlet-panelje ne térjen el se betűtípusban, se súlyban).
    { label: t('portal.tickets.columns.ticketNumber'), value: r.ticketNumber ?? '—' },
    // DÁTUM, nem időpont: a kibontóban a nap az információ (a pontos időpont a jegy-részleten van) — a
    // BELSŐ jegy-lista `ticketDetailRows`-a is `toLocaleDateString`-et használ.
    { label: t('portal.tickets.submittedAt'), value: openedDate(r.createdAt, locale) },
    // a LEZÁRÁS napja — csak ha ismert (nyitott vagy ismeretlen idejű régi jegynél a sor kimarad)
    ...(r.closedAt
      ? [{ label: t('portal.tickets.closedAt'), value: openedDate(r.closedAt, locale) }]
      : []),
    {
      label: t('portal.tickets.columns.requester'),
      value:
        formatName(
          {
            givenName: r.requesterGivenName,
            familyName: r.requesterFamilyName,
            displayName: r.requesterName,
          },
          locale,
        ) || '—',
    },
  ];
}

export const Route = createFileRoute('/portal/tickets/')({
  validateSearch: searchSchema,
  component: PortalTicketsPage,
});

const DEFAULTS = {
  tab: 'in_progress' as 'in_progress' | 'closed',
  q: '',
  type: '',
  status: '',
  from: '',
  to: '',
  closedFrom: '',
  closedTo: '',
};

type Row = PortalTicketRow;

/** A lap a jog-kapun át: jog nélkül magyarázat áll a lap helyett (a fal a BFF-ben van). */
function PortalTicketsPage(): JSX.Element {
  return (
    <PortalPermissionGate permission={PORTAL_PERMISSIONS.ticketsView}>
      <PortalTicketsPageBody />
    </PortalPermissionGate>
  );
}

function PortalTicketsPageBody(): JSX.Element {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { accountId } = usePortal();
  // Új jegyet csak az nyithat, akinek VALAMELY fiókján megvan a `ticketsWrite` (megtekintő: nincs gomb).
  const canOpenTicket = usePortalPermissions().has(PORTAL_PERMISSIONS.ticketsWrite);
  const { filters, setFilters } = useListFilters(DEFAULTS);
  const tab: 'in_progress' | 'closed' = filters.tab === 'closed' ? 'closed' : 'in_progress';
  // A LEZÁRÁS-tartomány csak a „Lezárt" fülön hat (a fülváltás amúgy is üríti).
  const closedFrom = tab === 'closed' ? filters.closedFrom : '';
  const closedTo = tab === 'closed' ? filters.closedTo : '';

  // `fill`: a lap KITÖLTI a keret magasságát, és a görgetés a táblán belül történik — a belső lista-oldalak
  // geometriája (a kártya alja egy vonalban a sidebar aljával, és a keret-`main`-ben nincs görgetősáv,
  // tehát a bal és a jobb margó egyenlő) — user-lelet.
  usePortalHead(
    {
      title: t('portal.tickets.title'),
      subtitle: t('portal.tickets.subtitle'),
      fill: true,
      // A MOBIL „+" FAB CSAK ITT: ez az egyetlen lap, ahol a partner új jegyet kezdeményezhet, és ahol a
      // sávban lévő „Új jegy" gomb mobilon nem látszik (user-döntés).
      newTicketFab: true,
    },
    [t('portal.tickets.title')],
  );

  // NYERS ts-rest hívás (nem a `useQuery` wrapper): így a válasz TELJES status-uniója látszik, és a 403-at
  // („nincs portál-hozzáférésed") saját állapotként tudjuk mutatni, nem hibaként.
  const q = useQuery({
    // A FIÓK-SCOPE a kulcs része: váltásnál új kérés kell (nem a régi lista szűrése kliens-oldalon).
    queryKey: portalKeys.ticketList({ ...filters, tab, account: accountId ?? '' }),
    queryFn: () =>
      api.portal.tickets.query({
        query: {
          tab,
          ...(filters.q ? { q: filters.q } : {}),
          // A SZŰKÍTÉS a sidebar fiók-váltójából jön — a lapnak NINCS saját fiók-szűrője (egy vezérlő,
          // egy igazság). `null` → nincs `accountId`, azaz „Minden fiók".
          ...(accountId !== null ? { accountId } : {}),
          ...(filters.type ? { type: filters.type as (typeof PORTAL_TICKET_TYPES)[number] } : {}),
          // A STÁTUSZ-szűrő a FÜLÖN BELÜL szűkít (a fül a durva bontás: folyamatban / lezárt).
          ...(filters.status
            ? { status: filters.status as (typeof PORTAL_TICKET_STATUSES)[number] }
            : {}),
          // DÁTUM-TARTOMÁNY a létrehozásra — a SZERVER szűr (nem a kliens szűkíti a lehozott lapot),
          // különben a lapozás és a fül-számlálók a szűrés ELŐTTI halmazt tükrözték volna.
          ...(filters.from ? { createdFrom: filters.from } : {}),
          ...(filters.to ? { createdTo: filters.to } : {}),
          ...(closedFrom ? { closedFrom } : {}),
          ...(closedTo ? { closedTo } : {}),
          limit: 50,
          lang: i18n.language === 'en' ? 'en' : 'hu',
        },
      }),
  });

  const body = q.data?.status === 200 ? q.data.body : undefined;
  const rows = body?.tickets ?? [];
  const isForbidden = q.data?.status === 403;
  const isError = !q.isLoading && q.data !== undefined && q.data.status !== 200 && !isForbidden;
  const activeFilters =
    (filters.type !== '' ? 1 : 0) +
    (filters.status !== '' ? 1 : 0) +
    (filters.from !== '' ? 1 : 0) +
    (filters.to !== '' ? 1 : 0) +
    (closedFrom !== '' ? 1 : 0) +
    (closedTo !== '' ? 1 : 0);
  const hasQuery = filters.q !== '' || activeFilters > 0;

  const columns: DataTableColumn<Row>[] = [
    {
      // 1. JEGY — a sor HORGONYA: tárgy + típus-chip · leírás-kivonat · összecsukott „Részletek" (jegyszám +
      // nyitás dátuma). A korábbi külön „Tartalom" oszlop MEGSZŰNT (user-döntés): a cím és a
      //    leírás így szélesebb, a sor 3 sornál nem magasabb, és a portál ugyanazt a mintát követi, mint a
      //    BELSŐ jegy-lista (horgony-oszlop + `DetailsExpander`, státusz a dedikált UTOLSÓ oszlopban).
      //    MIÉRT a „Részletek" alá a jegyszám: az azonosító visszakereséskor kell, nem böngészéskor — és a
      //    kereső a jegyszámra is illeszt, tehát semmi nem veszik el.
      id: 'ticket',
      header: t('portal.tickets.columns.ticket'),
      cardLabel: t('portal.tickets.columns.ticket'),
      card: 'title',
      // A HORGONY-oszlop rugalmas, de nem határtalan: a maradék helyet a többi oszlop kapja meg. Korábban
      // `2fr` volt, és emiatt az „Utolsó aktivitás" cellája vágta a hosszú relatív időt („körülbelül 4
      // órával ezelőtt") — user-lelet.
      width: 'minmax(240px,1.4fr)',
      multiline: true,
      // MOBIL-kártya: a jegyszám és a nyitás dátuma a kártya EGYETLEN „Részletek" rácsába kerül (a
      // `DetailsExpander` a kártyán nem jelenik meg — két egymás alatti kibontó félreérthető lenne).
      cardExtraItems: (r) => detailRows(r, t, i18n.language),
      // MOBIL-kártya fejléce: tárgy + a két jelvény (típus · státusz). A `DetailsExpander` a kártyán
      // SZÁNDÉKOSAN nem jelenik meg — a részlet-párokat a `cardExtraItems` adja a kártya egyetlen
      // „Részletek" rácsába, és két egymás alatti kibontó félreérthető lenne.
      cardCell: (r) => (
        <span className="flex min-w-0 flex-col items-start gap-1.5">
          <span
            className={cn(
              'min-w-0 truncate',
              r.unreadCommentCount > 0 && 'text-foreground font-medium',
            )}
          >
            {r.subject}
          </span>
          <span className="flex flex-wrap items-center gap-1.5">
            <TicketTypeBadge
              type={r.type}
              label={t(`portal.type.${r.type}`, { defaultValue: r.type })}
            />
            <TicketStatusBadge status={r.status} label={t(`portal.status.${r.status}`)} />
          </span>
        </span>
      ),
      cell: (r) => (
        <div className="flex min-w-0 flex-col items-start gap-1">
          <span className="flex min-w-0 flex-wrap items-center gap-2">
            {/* A TÁRGY a horgony. OLVASATLANUL `medium` súlyú: ez a jelzés ingyen van (nem foglal helyet), és
                a Gmail/Linear mintája — a SOR jelzi az olvasatlanságot, a SZÁM a meta-oszlopban áll. */}
            <span
              className={cn(
                'min-w-0 truncate',
                r.unreadCommentCount > 0 && 'text-foreground font-medium',
              )}
            >
              {r.subject}
            </span>
          </span>
          {/* MÁSODIK SOR = támogató adat: a lista-konvenció `tone="tertiary"`-je (halványabb + könnyebb).
              A törzs SIMA SZÖVEG — a kivonatot a szerver strippeli (`plainTextExcerpt`). */}
          <Text tone="tertiary" as="span" className="line-clamp-1 text-left">
            {r.descriptionExcerpt}
          </Text>
          {/* HARMADIK SOR: a kibontó — a legkevésbé fontos adat legalul (a jegyszám mono 11, halvány: itt
              már NEM horgony, hanem kód, amit karakterenként vetnek össze) —, MELLETTE a két jelvény:
              TÍPUS és STÁTUSZ (user-döntés, 2026-09-08). A partner-oldalon ezért NINCS dedikált
              státusz-oszlop: a szűkítést a szűrő-panel státusz-szűrője adja, a sor pedig egy helyen
              hordozza a jegy „mi ez / hol áll" párját. */}
          <DetailsExpander
            rows={detailRows(r, t, i18n.language)}
            trailing={
              <span className="flex flex-wrap items-center gap-1.5">
                <TicketTypeBadge
                  type={r.type}
                  label={t(`portal.type.${r.type}`, { defaultValue: r.type })}
                />
                <TicketStatusBadge status={r.status} label={t(`portal.status.${r.status}`)} />
              </span>
            }
          />
        </div>
      ),
    },
    {
      id: 'account',
      header: t('portal.tickets.columns.account'),
      cardLabel: t('portal.tickets.columns.account'),
      card: 'subtitle',
      width: '150px',
      // A fiók-név a cella-tipográfiát ÖRÖKLI (13 / muted) — a `secondary` variáns 12-es, ami ebben az
      // oszlopban kisebb volt a többinél (user-lelet). Ugyanez a minta a belső jegy-listán.
      cell: (r) => <span className="block min-w-0 truncate">{r.accountName}</span>,
    },
    {
      id: 'assignee',
      header: t('portal.tickets.columns.assignee'),
      cardLabel: t('portal.tickets.columns.assignee'),
      width: '132px',
      cell: (r) =>
        r.assigneeName === null ? (
          <Text variant="secondary" as="span">
            —
          </Text>
        ) : (
          <PersonChip
            name={r.assigneeName}
            avatarUrl={r.assigneeAvatarUrl}
            accentColor={r.assigneeTeamColor}
            size="sm"
          />
        ),
    },
    {
      // 4. UTOLSÓ AKTIVITÁS — a „mi történt mostanában" oszlop: relatív idő + a két SZÁMLÁLÓ (komment · új).
      // Az „X új" jelvény IDE került a cím mellől (user-döntés): a számok helye a meta-oszlop,
      //    a cím helyét a cím kapja — az olvasatlanságot a sor a tárgy súlyával jelzi.
      id: 'lastActivity',
      header: t('portal.tickets.columns.lastActivity'),
      cardLabel: t('portal.tickets.columns.lastActivity'),
      // A LEGHOSSZABB magyar relatív idő („körülbelül 1 hónappal ezelőtt") 13-as betűvel ~186px — a 148px-en
      // levágódott. A fix szélesség szándékos: a relatív idő NEM törhet két sorba (a sor magasságát a
      // horgony-oszlop adja), és `nowrap`-pel egy szűk cellában csak vágás lenne.
      width: '196px',
      multiline: true,
      cell: (r) => (
        <div className="flex min-w-0 flex-col items-start gap-1">
          {/* RELATÍV idő: a listában az érdekes, MENNYIRE friss — a pontos időpont a részleten van. A
              tipográfia a cella-alap (13 / muted): az időpont nem azonosító. */}
          <span className="block min-w-0 truncate">
            {formatRelativeTime(r.lastActivityAt, i18n.language)}
          </span>
          <span className="flex min-w-0 flex-wrap items-center gap-1.5">
            {/* A KOMMENT-SZÁM a soron a leghalványabb adat (11 / 300 / subtle — kisebb és könnyebb, mint a
                leírás-kivonat): egy puszta darabszám jelvényként túl sok hangsúlyt kapott. A szó „komment",
                nem „válasz": a szálon a partner és a kolléga hozzászólásai együtt állnak. */}
            {r.replyCount > 0 && (
              <Text tone="tertiary" as="span" className="text-xs">
                {t('portal.tickets.commentCount', { count: r.replyCount })}
              </Text>
            )}
            {/* Az OLVASATLAN-szám jelvényként (ez a TEENDŐ, nem statisztika): rövid alak + beszélgetés-ikon. */}
            {r.unreadCommentCount > 0 && (
              <Badge variant="brand" className="shrink-0" icon={<ChatBubbleLeftRightIcon />}>
                {t('portal.tickets.unreadShort', { count: r.unreadCommentCount })}
              </Badge>
            )}
          </span>
        </div>
      ),
    },
  ];
  return (
    // A GYÖKÉR magasság-kitöltő flex-oszlop: a fül-sáv és az eszköztár fix magasságú, a tábla (`flex-1
    // min-h-0`) veszi el a maradékot és GÖRGET — így a lap alja nem csúszik a viewport alá.
    //
    // NINCS `gap`: a szakaszok közét a `ListToolbar` SAJÁT `py-2.5`-e adja (10px), pontosan úgy, ahogy a
    // BELSŐ lista-oldalakon (ott a fül-sáv és az eszköztár is gap NÉLKÜL, közvetlenül követi egymást). A
    // korábbi `gap-3.5` ehhez még 14px-et adott, tehát a fülek↔kereső és a kereső↔tábla köz 24px volt —
    // user-döntés: „ha leveszem a gap-3.5-öt, sokkal kompaktabb érzést kelt".
    <div className="flex min-h-0 flex-1 flex-col">
      {/* PORTÁL-HOZZÁFÉRÉS NÉLKÜL a szerver 403-at ad — SAJÁT állapot, nem hiba: a partner nem „hibába"
          futott, hanem nincs még hozzáférése, és tudnia kell, kitől kérje. */}
      {isForbidden ? (
        <EmptyState title={t('portal.noAccess.title')} description={t('portal.noAccess.desc')} />
      ) : (
        <>
          {/* A FÜL-SÁV a saját sorában (a partner-terv `TabStrip`-je), ALATTA a KÖZÖS `ListToolbar`:
              kereső + „Szűrők (N)" popover + darabszám-meta + akció — pontosan az admin-listák geometriája
              (user-döntés, 2026-09-08: „a kereső és szűrés elhelyezés nem olyan szép, mint admin oldalon").
              A kézzel összeállított sáv helyett így a két felület egy KOMPONENSEN osztozik: a magasságok, a
              térközök és a popover-viselkedés nem tud elcsúszni. */}
          <div className="flex flex-wrap items-center gap-2">
            <TabStrip
              value={tab}
              // a lezárás-tartomány a „Lezárt" fül saját szűrője — máshová váltva nem marad rejtett, aktív szűrő
              onChange={(v) =>
                setFilters(
                  v === 'closed'
                    ? { tab: 'closed' }
                    : { tab: 'in_progress', closedFrom: '', closedTo: '' },
                )
              }
              // AZ IKONOK a BELSŐ jegy-lista fülein használtakkal EGYEZNEK (folyamatban → körkörös nyíl,
              // lezárt → pipa): a két felület ugyanazt az állapotot ugyanazzal a jellel mondja.
              items={[
                {
                  value: 'in_progress',
                  label: t('portal.tickets.tabs.in_progress'),
                  icon: <ArrowPathIcon />,
                  ...(body ? { count: body.counts.in_progress } : {}),
                },
                {
                  value: 'closed',
                  label: t('portal.tickets.tabs.closed'),
                  icon: <CheckCircleIcon />,
                  ...(body ? { count: body.counts.closed } : {}),
                },
              ]}
              aria-label={t('portal.tickets.title')}
            />
          </div>

          <ListToolbar
            // `px-0`: a sáv alap-paddingja a BELSŐ lista-oldalakhoz van hangolva (ott a szélig futó sticky-sáv
            // hozza vele a lap-margót). A portál-keret tartalom-sávjának MÁR van `px-3`/`md:px-4` margója,
            // tehát itt a `px-4` egy MÁSODIK behúzás lett volna: a kereső beljebb kezdődött, mint a fülek és a
            // kártyák (user-lelet).
            className="px-0"
            search={{
              value: filters.q,
              onChange: (v: string) => setFilters({ q: v }),
              placeholder: t('common.searchPlaceholder'),
              ariaLabel: t('portal.tickets.searchLabel'),
            }}
            // `filterItems` (és nem a `filters`/`dateFilters` prop-pár): ez SORREND-TARTÓ, tehát a
            // dátum-tartomány a típus és a státusz KÖZÉ kerül — pontosan ott, ahol a belső jegy-listán is
            // (user-kérés: „ugyanaz legyen a 2 dátum szűrő, mint admin oldalon"). A fajta
            // szerint rendező régi propokkal a dátumok a státusz UTÁN álltak volna.
            filterItems={[
              {
                kind: 'select',
                key: 'type',
                label: t('portal.newTicket.type'),
                value: filters.type === '' ? ALL : filters.type,
                onChange: (v: string) => setFilters({ type: v === ALL ? '' : v }),
                options: PORTAL_TICKET_TYPES.map((type) => ({
                  value: type,
                  label: t(`portal.type.${type}`),
                })),
                // A „mind" felirat a KÖZÖS `common.all`-ból jön (a `ListToolbar` alapja), tehát ugyanaz,
                // mint a belső lista-oldalakon — a korábbi „összes típus" saját megfogalmazás volt
                // (user-döntés: „nevezzük át kisbetűsen »mind«-re, mint admin oldalon").
                allValue: ALL,
              },
              // LÉTREHOZÁS-DÁTUM tartomány: ugyanaz a két `kind: 'date'` mező, mint a belső jegy-listán —
              // ugyanaz a komponens, ugyanaz a megjelenés. A szűrés a SZERVEREN fut, a `createdAt` NAPJÁRA,
              // inkluzívan mindkét végén.
              {
                kind: 'date',
                key: 'from',
                label: t('portal.tickets.filterFrom'),
                value: filters.from,
                onChange: (v: string) => setFilters({ from: v }),
              },
              {
                kind: 'date',
                key: 'to',
                label: t('portal.tickets.filterTo'),
                value: filters.to,
                onChange: (v: string) => setFilters({ to: v }),
              },
              // A „Lezárt" fülön a LEZÁRÁS ideje szerinti tartomány is (Évi #78).
              ...(tab === 'closed'
                ? [
                    {
                      kind: 'date' as const,
                      key: 'closedFrom',
                      label: t('portal.tickets.filterClosedFrom'),
                      value: closedFrom,
                      onChange: (v: string) => setFilters({ closedFrom: v }),
                    },
                    {
                      kind: 'date' as const,
                      key: 'closedTo',
                      label: t('portal.tickets.filterClosedTo'),
                      value: closedTo,
                      onChange: (v: string) => setFilters({ closedTo: v }),
                    },
                  ]
                : []),
              {
                // STÁTUSZ-szűrő: a partner-oldalon nincs státusz-OSZLOP (a jelvény a sor 1. oszlopában áll),
                // a szűkítést ez adja — a FÜLÖN BELÜL (a fül a durva bontás: folyamatban / lezárt).
                kind: 'select',
                key: 'status',
                label: t('portal.tickets.columns.status'),
                value: filters.status === '' ? ALL : filters.status,
                onChange: (v: string) => setFilters({ status: v === ALL ? '' : v }),
                options: PORTAL_TICKET_STATUSES.map((status) => ({
                  value: status,
                  label: t(`portal.status.${status}`),
                })),
                allValue: ALL,
              },
            ]}
            meta={
              body ? (
                <span aria-live="polite">
                  {t('portal.tickets.countShort', { count: body.total })}
                </span>
              ) : undefined
            }
            actions={
              // Az „Új jegy" CSAK ≥md: mobilon a keret LEBEGŐ gombja (`PortalShell` FAB, jobb alsó sarok) viszi
              // a jegy-nyitást, tehát a sávban ott csak egy második, helyet elvevő másolat lenne (user-lelet).
              // Ugyanez a minta a belső lista-oldalakon.
              canOpenTicket ? (
                <div className="hidden md:block">
                  <Button
                    variant="brand"
                    size="sm"
                    onClick={() => void navigate({ to: '/portal/tickets/new' })}
                  >
                    <PlusIcon />
                    {t('portal.newTicket.action')}
                  </Button>
                </div>
              ) : undefined
            }
          />

          {/* NINCS külön `Card` wrapper: a `DataTable` MAGA a kártya (`card-glass-lg`), mobilon pedig
              KÁRTYÁKAT rendereli a sorokból. A korábbi wrapper miatt egy NAGY kártyán belül ültek a kis
              kártyák (user-lelet, 2026-09-08) — az admin-listák sem wrappelik. */}
          {isError ? (
            <PortalErrorState onRetry={() => void q.refetch()} />
          ) : (
            <DataTable<Row>
              columns={columns}
              rows={rows}
              getRowId={(r) => r.id}
              onRowClick={(r) => void navigate({ to: '/portal/tickets/$id', params: { id: r.id } })}
              isLoading={q.isLoading}
              rowClassName={(r) =>
                // OLVASATLAN sor: bal szélén brand-jelölő + halvány tónus (a terv `.dt-r.unread`-je).
                r.unreadCommentCount > 0
                  ? 'relative bg-gradient-to-r from-brand/[0.07] to-transparent before:absolute before:left-0 before:top-2 before:bottom-2 before:w-[2.5px] before:rounded-full before:bg-brand before:content-[""]'
                  : undefined
              }
              emptyState={
                hasQuery ? (
                  <EmptyState
                    icon={MagnifyingGlassIcon}
                    title={t('portal.tickets.emptyFiltered.title')}
                    description={t('portal.tickets.emptyFiltered.desc')}
                    action={
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setFilters({ q: '', type: '', status: '' })}
                      >
                        {t('portal.tickets.clearFilters')}
                      </Button>
                    }
                  />
                ) : (
                  <EmptyState
                    title={t(`portal.tickets.empty.${tab}.title`)}
                    description={t(`portal.tickets.empty.${tab}.desc`)}
                    {...(tab === 'in_progress' && canOpenTicket
                      ? {
                          action: (
                            <Button
                              variant="brand"
                              size="sm"
                              onClick={() => void navigate({ to: '/portal/tickets/new' })}
                            >
                              <PlusIcon />
                              {t('portal.newTicket.action')}
                            </Button>
                          ),
                        }
                      : {})}
                  />
                )
              }
            />
          )}
        </>
      )}
    </div>
  );
}
